import { useEffect, useState, useContext } from 'react';
import api from '../api/api';
import {
  Grid, Card, CardContent, CardActions, Typography, CircularProgress, Box,
  Button, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Snackbar, Alert, Select, MenuItem, InputLabel, FormControl
} from '@mui/material';
import { AuthContext } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { servicesCatalog } from '../config/formsConfig';

// Genera el slug a partir del título del servicio, igual que formsConfig
const titleToSlug = (title = '') =>
  title.toLowerCase()
    .normalize('NFD').replace(/[\u0300-\u036f]/g, '')  // quita tildes
    .replace(/[^a-z0-9\s]/g, '')
    .trim()
    .replace(/\s+/g, '_');

// Busca el slug en el catálogo por similitud con el título de la DB
const getSlugForService = (title) => {
  const generated = titleToSlug(title);
  // Busca coincidencia exacta primero
  const exact = servicesCatalog.find(s => s.slug === generated);
  if (exact) return exact.slug;
  // Busca por inclusión del slug en el título generado o viceversa
  const partial = servicesCatalog.find(s =>
    generated.includes(s.slug) || s.slug.includes(generated)
  );
  return partial?.slug || null;
};

const Services = () => {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState({ open: false, id: null });
  const [formData, setFormData] = useState({ id: null, title: '', description: '', category: '', availableServices: '', icon: '', price: '' });
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');

  useEffect(() => { fetchServices(); }, []);

  const fetchServices = async () => {
    try {
      const res = await api.get('/services');
      setServices(res.data);
    } catch (error) {
      console.error('Error fetching services', error);
    } finally {
      setLoading(false);
    }
  };

  const uniqueCategories = [...new Set(services.map(s => s?.category).filter(Boolean))];

  const filteredServices = services.filter(s => {
    if (!s?.title) return false;
    const matchesSearch =
      s.title.toLowerCase().includes(search.toLowerCase()) ||
      s.description?.toLowerCase().includes(search.toLowerCase());
    const matchesCategory = categoryFilter ? s.category === categoryFilter : true;
    return matchesSearch && matchesCategory;
  });

  const getIconSafe = (icon) => {
    try { return require(`../assets/images/icons/${icon}`); }
    catch { return null; }
  };

  const handleInputChange = (e) => {
    setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleOpenDialog = (service = null) => {
    setFormData(service
      ? { id: service.id, title: service.title, description: service.description || '', category: service.category || '', availableServices: service.availableServices || '', icon: service.icon || '', price: service.price || '' }
      : { id: null, title: '', description: '', category: '', availableServices: '', icon: '', price: '' }
    );
    setDialogOpen(true);
  };

  const handleSubmit = async () => {
    if (formData.title.length < 3 || formData.description.length < 10 || !formData.category) {
      setSnackbar({ open: true, message: 'Por favor completá los campos correctamente.', severity: 'warning' });
      return;
    }
    const token = localStorage.getItem('token');
    try {
      if (formData.id) {
        const res = await api.put(`/services/${formData.id}`, formData, { headers: { Authorization: `Bearer ${token}` } });
        setServices(prev => prev.map(s => s.id === formData.id ? res.data : s));
        setSnackbar({ open: true, message: 'Servicio actualizado.', severity: 'success' });
      } else {
        const res = await api.post('/services', formData, { headers: { Authorization: `Bearer ${token}` } });
        setServices(prev => [...prev, res.data]);
        setSnackbar({ open: true, message: 'Servicio creado.', severity: 'success' });
      }
    } catch (err) {
      setSnackbar({ open: true, message: 'Error al guardar el servicio.', severity: 'error' });
    } finally {
      setDialogOpen(false);
    }
  };

  const handleDelete = async () => {
    const token = localStorage.getItem('token');
    try {
      await api.delete(`/services/${confirmDialog.id}`, { headers: { Authorization: `Bearer ${token}` } });
      setServices(prev => prev.filter(s => s.id !== confirmDialog.id));
      setSnackbar({ open: true, message: 'Servicio eliminado.', severity: 'success' });
    } catch {
      setSnackbar({ open: true, message: 'Error al eliminar.', severity: 'error' });
    } finally {
      setConfirmDialog({ open: false, id: null });
    }
  };

  const handleSolicitar = (service) => {
    const slug = getSlugForService(service.title);
    if (slug) {
      navigate(`/solicitar/${slug}`, { state: { serviceTitle: service.title } });
    } else {
      navigate(`/solicitar/otro`, { state: { serviceTitle: service.title } });
    }
  };

  return (
    <Box sx={{ flexGrow: 1 }}>
      <Typography variant="h5" gutterBottom sx={{ px: 2, pt: 2, fontWeight: 600, color: '#163244' }}>
        Servicios disponibles
      </Typography>

      {/* Filtros */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', px: 2, mb: 3, gap: 2 }}>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
          <TextField
            label="Buscar"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            size="small"
            sx={{ width: 250 }}
          />
          <FormControl size="small" sx={{ width: 200 }}>
            <InputLabel>Categoría</InputLabel>
            <Select value={categoryFilter} label="Categoría" onChange={(e) => setCategoryFilter(e.target.value)}>
              <MenuItem value="">Todas</MenuItem>
              {uniqueCategories.map(cat => <MenuItem key={cat} value={cat}>{cat}</MenuItem>)}
            </Select>
          </FormControl>
        </Box>
        {user?.role === 'admin' && (
          <Button
            variant="contained"
            sx={{ backgroundColor: '#E7A432', color: '#163244', fontWeight: 600 }}
            onClick={() => handleOpenDialog()}
          >
            + Agregar servicio
          </Button>
        )}
      </Box>

      {/* Cards */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6 }}><CircularProgress /></Box>
      ) : filteredServices.length > 0 ? (
        <Grid container spacing={2} sx={{ px: 2, pb: 4 }}>
          {filteredServices.map(s => {
            const icon = getIconSafe(s.icon);
            const slug = getSlugForService(s.title);
            return (
              <Grid item key={s.id} xs={12} sm={6} md={4} lg={3}>
                <Card sx={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'space-between',
                  borderRadius: 3,
                  boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                  transition: '0.25s',
                  '&:hover': { boxShadow: '0 6px 20px rgba(0,0,0,0.14)', transform: 'translateY(-2px)' }
                }}>
                  <CardContent sx={{ textAlign: 'center', pb: 1 }}>
                    {icon ? (
                      <Box component="img" src={icon} alt={s.title}
                        sx={{ width: 72, height: 72, objectFit: 'contain', mb: 1 }} />
                    ) : (
                      <Box sx={{ width: 72, height: 72, mx: 'auto', mb: 1, borderRadius: '50%',
                        backgroundColor: '#E7A432', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <Typography sx={{ fontSize: 28, color: '#163244', fontWeight: 700 }}>
                          {s.title.charAt(0)}
                        </Typography>
                      </Box>
                    )}
                    <Typography variant="h6" fontWeight={700} sx={{ color: '#163244', mb: 0.5 }}>
                      {s.title}
                    </Typography>
                    {s.description && (
                      <Typography variant="body2" color="text.secondary" sx={{
                        mb: 1,
                        display: '-webkit-box', WebkitLineClamp: 3,
                        WebkitBoxOrient: 'vertical', overflow: 'hidden'
                      }}>
                        {s.description}
                      </Typography>
                    )}
                    {s.category && (
                      <Box sx={{ display: 'inline-block', backgroundColor: '#f0f4f8',
                        borderRadius: 10, px: 1.5, py: 0.3, mb: 1 }}>
                        <Typography variant="caption" sx={{ color: '#163244', fontWeight: 600 }}>
                          {s.category}
                        </Typography>
                      </Box>
                    )}
                  </CardContent>

                  <CardActions sx={{ flexDirection: 'column', gap: 1, px: 2, pb: 2 }}>
                    <Button
                      fullWidth
                      variant="contained"
                      onClick={() => handleSolicitar(s)}
                      sx={{ backgroundColor: '#E7A432', color: '#163244', fontWeight: 700,
                        borderRadius: 2, '&:hover': { backgroundColor: '#c98d1f' } }}
                    >
                      Solicitar servicio
                    </Button>
                    {user?.role === 'admin' && (
                      <Box sx={{ display: 'flex', gap: 1, width: '100%' }}>
                        <Button fullWidth size="small" variant="outlined"
                          sx={{ borderColor: '#163244', color: '#163244' }}
                          onClick={() => handleOpenDialog(s)}>
                          Editar
                        </Button>
                        <Button fullWidth size="small" variant="outlined" color="error"
                          onClick={() => setConfirmDialog({ open: true, id: s.id })}>
                          Eliminar
                        </Button>
                      </Box>
                    )}
                  </CardActions>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      ) : (
        <Box sx={{ textAlign: 'center', mt: 8 }}>
          <Typography color="text.secondary">No se encontraron servicios.</Typography>
        </Box>
      )}

      {/* Dialog admin: crear/editar */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ backgroundColor: '#163244', color: '#fff' }}>
          {formData.id ? 'Editar servicio' : 'Agregar servicio'}
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>

          {/* Fila 1: Título + Categoría */}
          <Box sx={{ display: 'flex', gap: 2, mb: 1 }}>
            <TextField
              label="Título" name="title" size="small"
              sx={{ flex: 2 }} margin="dense"
              value={formData.title} onChange={handleInputChange}
              required
            />
            <TextField
              label="Categoría" name="category" size="small"
              sx={{ flex: 1 }} margin="dense"
              value={formData.category} onChange={handleInputChange}
              required
            />
            <TextField
              label="Precio estimado ($)" name="price" size="small"
              type="number" sx={{ flex: 1 }} margin="dense"
              value={formData.price} onChange={handleInputChange}
            />
          </Box>

          {/* Descripción */}
          <TextField
            label="Descripción" name="description" fullWidth
            size="small" margin="dense" multiline rows={3}
            value={formData.description} onChange={handleInputChange}
            required
          />

          {/* Servicios disponibles */}
          <TextField
            label="Servicios disponibles (uno por línea)" name="availableServices"
            fullWidth size="small" margin="dense" multiline rows={4}
            value={formData.availableServices} onChange={handleInputChange}
            helperText="Listá los subservicios que ofrece este rubro, uno por línea"
          />

          {/* Ícono */}
          <TextField
            label="Nombre del ícono" name="icon" fullWidth
            size="small" margin="dense"
            value={formData.icon} onChange={handleInputChange}
            helperText="Ej: plomero.png — debe estar en /assets/images/icons/"
          />

          {/* Preview del ícono si existe */}
          {formData.icon && (() => {
            try {
              const src = require(`../assets/images/icons/${formData.icon}`);
              return (
                <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Box component="img" src={src} alt="preview"
                    sx={{ width: 40, height: 40, objectFit: 'contain' }} />
                  <Typography variant="caption" color="text.secondary">
                    Vista previa del ícono
                  </Typography>
                </Box>
              );
            } catch { return null; }
          })()}
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDialogOpen(false)} variant="outlined"
            sx={{ borderColor: '#163244', color: '#163244' }}>Cancelar</Button>
          <Button variant="contained" onClick={handleSubmit}
            sx={{ backgroundColor: '#E7A432', color: '#163244' }}>Guardar</Button>
        </DialogActions>
      </Dialog>

      {/* Dialog confirmar eliminar */}
      <Dialog open={confirmDialog.open} onClose={() => setConfirmDialog({ open: false, id: null })}>
        <DialogTitle>¿Eliminar este servicio?</DialogTitle>
        <DialogActions>
          <Button onClick={() => setConfirmDialog({ open: false, id: null })}>Cancelar</Button>
          <Button variant="contained" color="error" onClick={handleDelete}>Eliminar</Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar({ ...snackbar, open: false })}>
        <Alert severity={snackbar.severity}>{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
};

export default Services;